/**
 * Pure view logic for the AI cost & Parseh pricing screen.
 *
 * Everything here is a function of (rows, view) with no React in it, for the
 * same reason `clinicsView.js` is: the rules that are easy to get subtly wrong —
 * where a NULL sorts, which chip narrows what, what a CSV must never contain —
 * are the ones a screenshot cannot show and a headless test can.
 *
 * Money and averages arrive from the BFF as STRINGS ("0.00420000") and a figure
 * that does not exist arrives as `null`. This module parses for ordering and
 * for export only; it never does arithmetic on them (design §11.1).
 */

import { PAGE_SIZE, SEARCH_MIN_CHARS } from '../constants/list';

/**
 * The six views, in tab order. «ویزیت‌ها» is LAST so the digit shortcuts of the
 * five that came before it (`1`–`5`) keep meaning what they meant (PM-0427).
 */
export const VIEWS = ['overview', 'models', 'services', 'policies', 'ledger', 'visits'];
export const WINDOWS = ['7', '30', '90'];
export const DEFAULT_WINDOW = '30';

/** Server page size for the ledger — the first server-paged list in the panel. */
export const LEDGER_PAGE_SIZE = PAGE_SIZE;

const DAY_MS = 86400000;

/**
 * `?w=30` → the half-open window `[now − 30 d, now)` as RFC 3339 strings, which
 * is what every `CostWindow` argument takes. Built in the client because the
 * URL carries a preset, not two instants: a bookmark of «last 30 days» must
 * mean the 30 days before whenever it is opened.
 */
export function windowRange(w, now = Date.now()) {
  const days = Number(w);
  const span = WINDOWS.includes(String(w)) ? days : Number(DEFAULT_WINDOW);
  const to = new Date(now);
  const from = new Date(now - span * DAY_MS);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * A wire string → a finite number, or null. `null`, '' and junk all read as
 * "no figure" so a comparator never sees NaN.
 */
export function num(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Fold Persian and Arabic-Indic digits to ASCII so a typed id matches. */
const ASCII_DIGITS = (() => {
  const map = new Map();
  for (const base of [0x06f0, 0x0660]) {
    for (let d = 0; d <= 9; d += 1) map.set(String.fromCharCode(base + d), String(d));
  }
  return map;
})();
const foldDigits = (text) => String(text ?? '').replace(/./gu, (ch) => ASCII_DIGITS.get(ch) ?? ch);

const norm = (text) => foldDigits(text).trim().toLowerCase();

/**
 * Sort with the ABSENT values partitioned out first, so they land last in
 * BOTH directions. Handling it inside the comparator does not work: the
 * comparator's result is multiplied by the direction, so «missing sorts last»
 * inverts under `desc` and the unpriced rows jump to the top of a cost sort —
 * exactly where the expensive ones belong (see `clinicsView.js`).
 */
export function sortWithNulls(rows, valueOf, dir = 'desc', tieBreak) {
  const sign = dir === 'asc' ? 1 : -1;
  const present = [];
  const absent = [];
  for (const row of rows) (valueOf(row) == null ? absent : present).push(row);
  present.sort((a, b) => {
    const d = valueOf(a) - valueOf(b);
    if (d !== 0) return d * sign;
    return tieBreak ? tieBreak(a, b) : 0;
  });
  return [...present, ...absent];
}

/** Severity order for the alert zone: the dangerous ones first, stable within. */
const SEVERITY_RANK = { danger: 0, warning: 1, info: 2 };

export function sortAlerts(alerts) {
  return [...(alerts || [])]
    .map((alert, index) => ({ alert, index }))
    .sort((a, b) => {
      const d = (SEVERITY_RANK[a.alert.severity] ?? 3) - (SEVERITY_RANK[b.alert.severity] ?? 3);
      return d !== 0 ? d : a.index - b.index;
    })
    .map(({ alert }) => alert);
}

/**
 * Where each alert kind sends the operator. Every alert is a door into the view
 * that can fix it (dashboard doctrine: every number is a door).
 */
export const ALERT_TARGET = {
  no_active_policy: { view: 'policies' },
  unpriced_models: { view: 'models' },
  provider_drift: { view: 'models' },
  wallet_value_changed: { view: 'policies' },
  negative_margin: { view: 'services', sort: 'margin', dir: 'asc' },
  apps_mismatch: { view: 'services', mismatch: '1' },
  telemetry_dropped: { view: 'ledger' },
  deprecated_in_use: { view: 'models', deprecated: '1' },
  // PM-0427 — the nightly dollar. A stale rate is read in the policies view's
  // «نرخ دلار شبانه» history; a held night is a DRAFT a person must look at,
  // so that door opens the draft itself (`alertTarget` adds its id).
  fx_stale: { view: 'policies' },
  fx_reprice_held: { view: 'policies' },
};

/**
 * Where ONE alert sends the operator: `ALERT_TARGET` for its kind, plus what
 * only the alert itself knows — `fx_reprice_held` names the held draft in its
 * `detail`, and the door opens that draft rather than the list. Null for a kind
 * this build has no door for.
 */
export function alertTarget(alert) {
  const target = ALERT_TARGET[alert?.kind];
  if (!target) return null;
  if (alert.kind === 'fx_reprice_held') {
    const id = foldDigits(alert.detail).trim();
    return /^\d+$/.test(id) ? { ...target, policy: id } : target;
  }
  return target;
}

// ── services ─────────────────────────────────────────────────────────────────

const SERVICE_SORTERS = {
  invocations: (r) => r.invocations ?? null,
  cost: (r) => num(r.meanCostUsd),
  margin: (r) => num(r.marginPct),
  revenue: (r) => num(r.revenueParseh),
  price: (r) => num(r.effectiveParseh),
};

export const SERVICE_SORT_KEYS = Object.keys(SERVICE_SORTERS);

function serviceMatches(row, q, label) {
  const needle = norm(q);
  if (!needle) return true;
  return norm(label(row)).includes(needle) || norm(row.serviceKey).includes(needle);
}

/**
 * @param rows  every service row, as the BFF returned it
 * @param view  { q, sort, dir, negative, unpriced, mismatch } from the URL
 * @param opts  { label: (row) => string } — how the reader's language names a service
 */
export function servicesView(rows, view, { label = (r) => r.labelFa } = {}) {
  const all = Array.isArray(rows) ? rows : [];
  const q = String(view.q || '');
  const searching = q.trim().length >= SEARCH_MIN_CHARS;

  let out = all;
  if (view.negative === '1') out = out.filter((r) => (num(r.marginPct) ?? 0) < 0);
  if (view.unpriced === '1') out = out.filter((r) => r.hasUnpricedModel);
  if (view.mismatch === '1') out = out.filter((r) => r.appsMismatch);
  if (searching) out = out.filter((r) => serviceMatches(r, q, label));

  const sortKey = SERVICE_SORTERS[view.sort] ? view.sort : 'cost';
  const dir = view.dir === 'asc' ? 'asc' : 'desc';
  out = sortWithNulls(
    out, SERVICE_SORTERS[sortKey], dir,
    (a, b) => String(a.serviceKey).localeCompare(String(b.serviceKey)),
  );

  return {
    rows: out,
    total: all.length,
    filtered: out.length,
    isFiltered: searching || view.negative === '1' || view.unpriced === '1' || view.mismatch === '1',
  };
}

// ── models ───────────────────────────────────────────────────────────────────

const MODEL_STATUS_RANK = { unknown: 0, active: 1, deprecated: 2 };

function modelMatches(row, q) {
  const needle = norm(q);
  if (!needle) return true;
  return norm(row.modelId).includes(needle)
    || norm(row.displayName).includes(needle)
    || norm(row.provider).includes(needle);
}

/**
 * Unpriced (`unknown`) models FIRST whatever the sort: they are the rows with a
 * primary action, and the alert that brought the operator here points at them.
 * Within a status band, cost in the window, descending, NULLs last.
 */
export function modelsView(rows, view) {
  const all = Array.isArray(rows) ? rows : [];
  const q = String(view.q || '');
  const searching = q.trim().length >= SEARCH_MIN_CHARS;

  let out = all;
  if (view.deprecated !== '1') out = out.filter((r) => r.status !== 'deprecated');
  if (searching) out = out.filter((r) => modelMatches(r, q));

  const dir = view.dir === 'asc' ? 'asc' : 'desc';
  const valueOf = view.sort === 'calls' ? (r) => r.callsInWindow ?? null : (r) => num(r.costUsdInWindow);
  const sorted = sortWithNulls(out, valueOf, dir, (a, b) => String(a.modelId).localeCompare(String(b.modelId)));
  const banded = [...sorted].sort(
    (a, b) => (MODEL_STATUS_RANK[a.status] ?? 1) - (MODEL_STATUS_RANK[b.status] ?? 1),
  );

  return {
    rows: banded,
    total: all.length,
    filtered: banded.length,
    isFiltered: searching || view.deprecated === '1',
  };
}

export const MODEL_SORT_KEYS = ['cost', 'calls'];

// ── price source ─────────────────────────────────────────────────────────────

/**
 * `priceSource` on the wire is `policy:<id>` | `code_default` | `override`.
 * Returns `{ kind, id }` so a badge can say «سیاست ۱۲» without string surgery
 * at every render site.
 */
export function parsePriceSource(source) {
  const text = String(source || '');
  if (text.startsWith('policy:')) return { kind: 'policy', id: text.slice('policy:'.length) };
  if (text === 'override') return { kind: 'override', id: null };
  return { kind: 'code_default', id: null };
}

// ── ledger ───────────────────────────────────────────────────────────────────

export const LEDGER_STATUSES = ['ok', 'error', 'aborted'];
export const OWNER_TYPES = ['doctor', 'clinic', 'student'];

/**
 * The URL's ledger filters → the `AiInvocationFilter` input. A custom
 * `dfrom`/`dto` (Gregorian `YYYY-MM-DD` from the picker) overrides the preset
 * window on that end only; the range stays half-open, so «تا ۱۲ مرداد» includes
 * the whole of that day.
 */
export function ledgerFilter(view, now = Date.now()) {
  const preset = windowRange(view.w, now);
  const from = view.dfrom ? new Date(`${view.dfrom}T00:00:00+03:30`).toISOString() : preset.from;
  const to = view.dto ? new Date(new Date(`${view.dto}T00:00:00+03:30`).getTime() + DAY_MS).toISOString() : preset.to;
  const filter = { window: { from, to } };
  if (view.service) filter.serviceKey = view.service;
  if (view.model) filter.modelId = view.model;
  if (view.owner) filter.ownerType = view.owner;
  if (view.ownerId) filter.ownerId = foldDigits(view.ownerId).trim();
  if (view.status) filter.status = view.status;
  if (view.charged === 'yes' || view.charged === 'no') filter.charged = view.charged;
  return filter;
}

export function ledgerIsFiltered(view) {
  return !!(view.service || view.model || view.owner || view.ownerId || view.status
    || view.charged || view.dfrom || view.dto);
}

export function pageCountOf(total, pageSize = LEDGER_PAGE_SIZE) {
  return Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
}

// ── visits (PM-0427) ─────────────────────────────────────────────────────────

/**
 * The billed-minute buckets, in the service's closed order (`VisitMinutesBucket`):
 * `none` is a visit with no billed recording; a number is exactly that many
 * minutes; a range is inclusive; `31+` is open. Each maps to the filter that
 * selects it, so a row of the minutes table and the «دقیقهٔ ضبط» select are one
 * vocabulary.
 */
export const MINUTE_BUCKETS = ['none', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11-15', '16-20', '21-30', '31+'];

/** A bucket key → `{ recorded, min, max }` (0 = no bound), or null for junk. */
export function bucketRange(bucket) {
  const key = String(bucket || '');
  if (!MINUTE_BUCKETS.includes(key)) return null;
  if (key === 'none') return { recorded: 'no', min: 0, max: 0 };
  if (key.endsWith('+')) return { recorded: '', min: Number(key.slice(0, -1)), max: 0 };
  const [lo, hi = lo] = key.split('-').map(Number);
  return { recorded: '', min: lo, max: hi };
}

/** The server sorts the visit list; these are the keys it accepts (`sort`). */
export const VISIT_SORT_KEYS = ['started', 'earned', 'paid', 'margin', 'minutes'];
export const DEFAULT_VISIT_SORT = 'started';

/**
 * The URL's sort → the `sort` argument of `aiVisits`: «key:dir», which is how
 * the admin BFF carries the proto's two fields in one (`started:desc` when the
 * URL says nothing, i.e. newest first).
 */
export function visitSortArg(view) {
  const key = VISIT_SORT_KEYS.includes(view.sort) ? view.sort : DEFAULT_VISIT_SORT;
  const dir = view.dir === 'asc' ? 'asc' : 'desc';
  return `${key}:${dir}`;
}

/**
 * The URL's visit filters → the `AiVisitFilter` input. The window is NOT here:
 * it is the query's own `window` argument. An absent filter is an absent key,
 * not an empty string, so the BFF never has to guess what "" meant.
 */
export function visitsFilter(view) {
  const filter = {};
  if (view.owner) filter.ownerType = view.owner;
  if (view.ownerId) filter.ownerId = foldDigits(view.ownerId).trim();
  if (view.service) filter.serviceKey = view.service;
  const range = bucketRange(view.mins);
  if (range) {
    if (range.recorded) filter.recorded = range.recorded;
    if (range.min) filter.minBilledMinutes = range.min;
    if (range.max) filter.maxBilledMinutes = range.max;
  }
  if (!filter.recorded && (view.rec === 'yes' || view.rec === 'no')) filter.recorded = view.rec;
  return filter;
}

export function visitsIsFiltered(view) {
  return !!(view.owner || view.ownerId || view.service || view.rec || view.mins);
}

/**
 * A decimal string → `{ digits: BigInt, scale }` with value = digits / 10^scale,
 * or null. Accepts what the wire sends — «0.00420000», «278000», «-12», and an
 * exponent form in case one ever arrives.
 */
function parseDecimal(value) {
  const text = foldDigits(value).trim();
  const m = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(text);
  if (!m || (!m[2] && !m[3])) return null;
  const frac = m[3] || '';
  let digits = BigInt(`${m[2] || '0'}${frac}` || '0');
  let scale = frac.length - Number(m[4] || 0);
  if (scale < 0) {
    digits *= BigInt(`1${'0'.repeat(-scale)}`);
    scale = 0;
  }
  return { digits: m[1] === '-' ? -digits : digits, scale };
}

/**
 * `a × b` for two wire decimals, EXACTLY, as a decimal string — or null when
 * either is absent. This is the one conversion the client makes, and only
 * because the contract leaves it here: the visit RPCs answer in پارسه and USD
 * and echo the rate of the policy they were asked about («Toman is the
 * caller's», ai_cost_admin.proto §visits), and the admin BFF does no arithmetic.
 * BigInt rather than floating point, so 0.0254513 × 278000 is 7075.4614 and not
 * a neighbour of it (design §16: no floating-point arithmetic on money).
 */
export function mulDecimal(a, b) {
  const x = parseDecimal(a);
  const y = parseDecimal(b);
  if (!x || !y) return null;
  const product = x.digits * y.digits;
  const scale = x.scale + y.scale;
  const negative = product < BigInt(0);
  const raw = (negative ? -product : product).toString().padStart(scale + 1, '0');
  const whole = raw.slice(0, raw.length - scale);
  const frac = scale ? raw.slice(raw.length - scale).replace(/0+$/, '') : '';
  return `${negative ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
}

/**
 * How a visit's money converts to toman at the policy the answer names
 * (`AiVisitMoney`): پارسه × the Parseh's toman value, USD × the dollar rate.
 * Both answer null when there is no rate — no policy, or a credit not sold in
 * toman — so the cell says «—» rather than a toman figure nobody can check.
 */
export function visitToman(money) {
  const irt = !money?.creditCurrency || money.creditCurrency === 'IRT';
  const parsehRate = irt ? money?.parsehToman : null;
  const usdRate = irt ? money?.fxTomanPerUsd : null;
  return {
    irt,
    hasRate: num(usdRate) != null && num(parsehRate) != null,
    fromParseh: (parseh) => (num(parsehRate) == null ? null : mulDecimal(parseh, parsehRate)),
    fromUsd: (usd) => (num(usdRate) == null ? null : mulDecimal(usd, usdRate)),
  };
}

/** Rows per page of the nightly dollar's history. */
export const FX_PAGE_SIZE = 10;

// ── CSV ──────────────────────────────────────────────────────────────────────

/**
 * RFC 4180 cell: quoted when it holds a comma, a quote or a newline; quotes
 * doubled. `null`/`undefined` become the EMPTY cell, not the word "null".
 */
function csvCell(value) {
  if (value == null) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Build a CSV from `columns` ([{ key, header, value(row) }]) and `rows`.
 *
 * The file is for a spreadsheet, not a reader: LATIN digits and ISO dates
 * whatever the panel's locale (design F15). Column values are therefore raw
 * wire strings or numbers — the caller must NOT pass a `formatNumber`ed cell.
 * Headers are the reader's words, because those are what the operator will
 * see at the top of the sheet.
 */
export function toCsv(columns, rows) {
  const lines = [columns.map((c) => csvCell(c.header)).join(',')];
  for (const row of rows || []) {
    lines.push(columns.map((c) => csvCell(foldDigits(c.value(row)))).join(','));
  }
  return `${lines.join('\r\n')}\r\n`;
}

// ── policy editor ────────────────────────────────────────────────────────────

// `actual` (PM-0427): what the providers billed, priced per charged run — the
// "real price + 30 %" policy, the 30 % as overhead.
export const COST_BASES = ['mean', 'p50', 'p90', 'declared', 'actual'];
/**
 * `ceil_0_1` (PM-0427) rounds UP to the next tenth — a price is 3 or 3.1, never
 * 3.21 — and is what a new draft starts with, as the service's own default is.
 */
export const ROUNDINGS = ['ceil_0_1', 'ceil_int', 'nearest_int', 'step_5'];
export const DEFAULT_ROUNDING = 'ceil_0_1';

/**
 * Whether a typed price is a whole number of TENTHS (PM-0427): «3», «3.1» and
 * «3.10» are; «3.21» is not. The service refuses anything else for an override
 * or a minimum price, so the field says so before the round trip. '' (no figure)
 * passes: absence is the "clear" gesture, not a malformed price.
 */
export function isTenths(value) {
  const text = foldDigits(value).trim();
  if (text === '') return true;
  const m = /^[+-]?(\d*)(?:\.(\d*))?$/.exec(text);
  if (!m || (m[1] === '' && (m[2] == null || m[2] === ''))) return false;
  return (m[2] || '').replace(/0+$/, '').length <= 1;
}
export const POLICY_WINDOWS = [7, 30, 90];

/** The `AiPricingPolicyInput` shape, with the panel's defaults for a blank draft. */
export function blankPolicyInput({ fxTomanPerUsd = '' } = {}) {
  return {
    name: '',
    fxTomanPerUsd: fxTomanPerUsd || '',
    targetMarginPct: '85',
    overheadPct: '0',
    costBasis: 'mean',
    windowDays: 30,
    rounding: DEFAULT_ROUNDING,
    minParseh: '1',
    note: '',
  };
}

/** A stored policy → the editable input (the snapshot fields stay behind). */
export function policyToInput(policy) {
  if (!policy) return blankPolicyInput();
  return {
    name: policy.name || '',
    fxTomanPerUsd: policy.fxTomanPerUsd || '',
    targetMarginPct: policy.targetMarginPct || '',
    overheadPct: policy.overheadPct || '0',
    costBasis: policy.costBasis || 'mean',
    windowDays: Number(policy.windowDays) || 30,
    rounding: policy.rounding || DEFAULT_ROUNDING,
    minParseh: policy.minParseh || '1',
    note: policy.note || '',
  };
}

/**
 * Whether two inputs describe the same policy. Numbers are compared as numbers
 * so «85» and «85.0» are not a phantom edit that disables activation.
 */
export function sameInput(a, b) {
  if (!a || !b) return false;
  const numeric = ['fxTomanPerUsd', 'targetMarginPct', 'overheadPct', 'minParseh', 'windowDays'];
  for (const key of Object.keys(blankPolicyInput())) {
    const x = a[key];
    const y = b[key];
    if (numeric.includes(key)) {
      const nx = num(x);
      const ny = num(y);
      if (nx !== ny) return false;
    } else if (String(x ?? '') !== String(y ?? '')) return false;
  }
  return true;
}

/**
 * Whether `locale` writes a thousand as «1.000» — German. There a «.» in a typed
 * figure is a group mark, but the kit pinned today reads it as a decimal point,
 * and an English-style «100,000» is a decimal under any comma-decimal language.
 */
export function groupsWithDot(locale) {
  if (!locale) return false;
  try {
    const group = new Intl.NumberFormat(locale).formatToParts(1234567).find((p) => p.type === 'group');
    return group?.value === '.';
  } catch {
    return false;
  }
}

/**
 * Field-level problems with a policy input, keyed by field → message key.
 *
 * `withFx: false` is a deployment whose credit is NOT priced in toman (PM-0211:
 * USD on Proxia): cost in credits = loaded USD ÷ the credit's dollar price, so
 * there is no USD rate to ask for.
 *
 * `locale` is the panel language. Under a language that groups with «.» an
 * exchange rate below 1000 WITH a fractional part is refused (PM-0212): no real
 * Toman/USD rate looks like that, and it is exactly what a grouped «100.000» or a
 * «100,000» turns into when read as a decimal — every service price derived from
 * the policy would be a thousand times too low. A plain «100» is not second-guessed.
 */
export function validatePolicyInput(input, { withFx = true, locale } = {}) {
  const errors = {};
  if (!String(input.name || '').trim()) errors.name = 'aiPricing.policies.editor.nameRequired';
  if (withFx) {
    const fx = num(input.fxTomanPerUsd);
    if (fx == null || fx <= 0) errors.fxTomanPerUsd = 'aiPricing.policies.editor.fxRequired';
    else if (fx < 1000 && String(input.fxTomanPerUsd).includes('.') && groupsWithDot(locale)) {
      errors.fxTomanPerUsd = 'aiPricing.policies.editor.fxGrouped';
    }
  }
  const margin = num(input.targetMarginPct);
  if (margin == null || margin < 0 || margin > 99) errors.targetMarginPct = 'validation.required';
  // A minimum price is a price: tenths only, like an override (PM-0427).
  if (!isTenths(input.minParseh)) errors.minParseh = 'aiPricing.policies.editor.tenthsOnly';
  return errors;
}

/**
 * The input as the mutation wants it: strings for money/percent, Int for the
 * window. Without FX (`withFx: false`) the rate travels as "" — none — rather
 * than a stale figure copied from an older toman policy.
 */
export function serialisePolicyInput(input, { withFx = true } = {}) {
  return {
    name: String(input.name || '').trim(),
    fxTomanPerUsd: withFx ? String(input.fxTomanPerUsd) : '',
    targetMarginPct: String(input.targetMarginPct),
    overheadPct: String(input.overheadPct ?? '0'),
    costBasis: input.costBasis,
    windowDays: Number(input.windowDays),
    rounding: input.rounding,
    minParseh: String(input.minParseh ?? '1'),
    note: String(input.note || '').trim() || null,
  };
}

export default servicesView;
